<!-- UserForm.vue -->
<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from "vue";
import { toTitleCase } from "@/utils/stringUtils";
import { formatDateTimeForDisplay } from "@/utils/dateUtils";
import api from "@/services/api";

// Store
import { useUserStore } from "@/stores/useUserStore";

// Componentes modais
import AddUserForm from "./AddUserForm.vue";
import EditUserForm from "./EditUserForm.vue";
import DeleteUserForm from "./DeleteUserForm.vue";
import UserCardModal from "../User/UserCardModal.vue";
import BatchDeleteUserForm from "./BatchDeleteUserForm.vue";
import SearchUserInput from "./SearchUserInput.vue";

// Paginação
import PaginationButtons from "../Generics/PaginationButtons.vue";
import PaginationInfo from "../Generics/PaginationInfo.vue";

import UserHelpModal from "./UserHelpModal.vue";

const userStore = useUserStore();

// Estados de exibição dos modais
const showAddModal = ref(false);
const showEditModal = ref(false);
const showDeleteModal = ref(false);
const showBatchDeleteModal = ref(false);

// Card modal
const showCardModal = ref(false);
const selectedUserId = ref(null);

// Modal de ajuda
const showHelpModal = ref(false);

// Paginação
const currentPage = ref(1);
const pageSize = ref(10);

// [NOVO] loading visual da tabela
const isLoadingTable = ref(false);

// Lógica de Dropdowns e Seleção
const openDropdownId = ref(null);
const isBatchDropdownOpen = ref(false);
const batchDropdownRef = ref(null);

// ====================================================================
// ✅ TOAST PADRÃO (igual TextForm.vue)
// ====================================================================
const showToast = ref(false);
const toastMessage = ref("");
const toastTimeoutId = ref(null);
const toastType = ref("error"); // "error" | "success"

function resetToastTimeout() {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
    toastTimeoutId.value = null;
  }, 3000);
}

function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

function showSuccessToast(message) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}
// ====================================================================

/* =========================================================
   ✅ STATUS REAL DO BACKEND (UserStatus enum)
   status: "pending" | "active" | "inactive" | "blocked"
   (ou eventualmente 0..3)
========================================================= */
function normalizeUserStatus(status) {
  if (status === undefined || status === null) return "pending";

  if (typeof status === "number") {
    return ["pending", "active", "inactive", "blocked"][status] ?? "pending";
  }

  if (typeof status === "string") {
    const s = status.trim().toLowerCase();
    if (["pending", "active", "inactive", "blocked"].includes(s)) return s;
  }

  return "pending";
}

function getUserStatusLabel(status) {
  const s = normalizeUserStatus(status);
  if (s === "active") return "Ativo";
  if (s === "inactive") return "Inativo";
  if (s === "blocked") return "Bloqueado";
  return "Pendente";
}

function getUserStatusClass(status) {
  const s = normalizeUserStatus(status);
  if (s === "active") return "status-tag status-green";
  if (s === "inactive") return "status-tag status-red";
  if (s === "blocked") return "status-tag status-purple";
  return "status-tag status-yellow";
}
/* ========================================================= */

// permissões
function canEdit() {
  return true;
}
function canView() {
  return true;
}
function canDelete() {
  return true;
}

function normalizeAvatarPath(path) {
  if (!path) return "";

  if (path.startsWith("http://") || path.startsWith("https://")) return path;

  const base = api.defaults.baseURL.replace(/\/api$/, "").replace(/\/+$/, "");
  const cleanPath = path.startsWith("/") ? path : `/${path}`;

  return `${base}${cleanPath}`;
}

function getAvatarUrl(photoPath) {
  if (!photoPath) return "";
  return normalizeAvatarPath(photoPath);
}

function resolveUserPhotoPath(user) {
  if (!user) return null;

  return (
    user.photoPath ||
    user.photoUrl ||
    user.userPhotoPath ||
    user.userPhotoUrl ||
    user.photo_path ||
    user.user_photo_path ||
    null
  );
}

const anyEditableOnPage = computed(() =>
  userStore.users.some((u) => canEdit(u))
);

const isAllSelectedOnPage = computed({
  get: () => {
    const editableIdsOnPage = userStore.users
      .filter((u) => canEdit(u))
      .map((u) => u.userId);

    if (editableIdsOnPage.length === 0) return false;

    return editableIdsOnPage.every((id) =>
      userStore.selectedUserIds.includes(id)
    );
  },
  set: (value) => {
    const editableIdsOnPage = userStore.users
      .filter((u) => canEdit(u))
      .map((u) => u.userId);

    const allIdsOnPage = userStore.users.map((u) => u.userId);

    if (value) {
      editableIdsOnPage.forEach((id) => {
        if (!userStore.selectedUserIds.includes(id)) {
          userStore.selectedUserIds.push(id);
        }
      });
    } else {
      userStore.selectedUserIds = userStore.selectedUserIds.filter(
        (id) => !allIdsOnPage.includes(id)
      );
    }
  },
});

const canBatchActions = computed(() => {
  return (
    Array.isArray(userStore.selectedUserIds) &&
    userStore.selectedUserIds.length > 0
  );
});

function toggleRowDropdown(userId) {
  openDropdownId.value = openDropdownId.value === userId ? null : userId;
}

function handleEdit(index) {
  indexSelector(index);
  showEditModal.value = true;
  openDropdownId.value = null;
}

function handleView(index) {
  indexSelector(index);
  selectedUserId.value = userStore.selectedUser?.userId ?? null;
  showCardModal.value = true;
  openDropdownId.value = null;
}

function handleDelete(index) {
  indexSelector(index);
  showDeleteModal.value = true;
  openDropdownId.value = null;
}

function handleRowClick(index) {
  indexSelector(index);
  selectedUserId.value = userStore.selectedUser?.userId ?? null;
  showCardModal.value = true;
  openDropdownId.value = null;
}

function toggleSelection(id) {
  userStore.toggleUserSelection(id);
}

async function fetchUsers() {
  try {
    isLoadingTable.value = true;
    await userStore.fetchUsers(
      currentPage.value,
      pageSize.value,
      userStore.userSearchTerm
    );
  } catch (error) {
    console.error("❌ Erro ao buscar usuários paginados:", error?.message);
    showErrorToast("Erro ao carregar usuários.");
  } finally {
    isLoadingTable.value = false;
  }
}

watch(currentPage, fetchUsers);

function tryOpenBatchDeleteModal() {
  const selectedIds = userStore.selectedUserIds;

  if (!Array.isArray(selectedIds) || selectedIds.length === 0) {
    showErrorToast("Nenhum usuário selecionado.");
    isBatchDropdownOpen.value = false;
    return;
  }

  showBatchDeleteModal.value = true;
  isBatchDropdownOpen.value = false;
}

// ====================================================================
// ✅ Handler único pós-mudança (padrão TextForm.vue)
// ====================================================================
async function handleUsersChanged(options = {}) {
  const {
    goToFirstPage = true,
    closeAdd = true,
    closeEdit = true,
    closeDelete = true,
    closeBatchDelete = true,
    clearSelection = false,
    successMessage = null,
  } = options;

  if (closeAdd) showAddModal.value = false;
  if (closeEdit) showEditModal.value = false;
  if (closeDelete) showDeleteModal.value = false;
  if (closeBatchDelete) showBatchDeleteModal.value = false;

  if (clearSelection) {
    userStore.selectedUserIds = [];
  }

  if (successMessage) {
    showSuccessToast(successMessage);
  }

  if (goToFirstPage) {
    if (currentPage.value !== 1) {
      currentPage.value = 1;
      return;
    }
    await fetchUsers();
    return;
  }

  await fetchUsers();
}
// ====================================================================

function handleClickOutside(event) {
  if (
    batchDropdownRef.value &&
    !batchDropdownRef.value.contains(event.target)
  ) {
    isBatchDropdownOpen.value = false;
  }
  if (openDropdownId.value && !event.target.closest(".action-cell-container")) {
    openDropdownId.value = null;
  }
}

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
  fetchUsers();
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);

  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);

  userStore.userSearchTerm = "";
  userStore.currentPage = 1;
  userStore.shouldForcePageOne = true;

  userStore.selectedUserIds = [];
  userStore.selectedUser = null;
  openDropdownId.value = null;
  isBatchDropdownOpen.value = false;
  selectedUserId.value = null;

  showAddModal.value = false;
  showEditModal.value = false;
  showDeleteModal.value = false;
  showBatchDeleteModal.value = false;
  showCardModal.value = false;
  showHelpModal.value = false;

  isLoadingTable.value = false;
});

function indexSelector(index) {
  const user = userStore.users[index];
  userStore.setSelectedUser(user);
}
</script>

<template>
  <div class="w-full h-full">
    <div class="main-container">
      <div class="main-header">
        <div class="flex flex-col gap-0">
          <div class="text-xl font-bold">Usuários</div>
          <div class="text-sm mt-1 text-gray-700 ">
            Gerencie os usuários do sistema, além de acompanhar contadores de
            textos, notas e o último endereço IP registrado de cada conta.
            <button
              class="underline text-blue-600 hover:text-blue-800 ml-1"
              @click="showHelpModal = true"
            >
              Saiba mais
            </button>
          </div>
        </div>
      </div>

      <div class="action-bar">
        <div class="w-full md:w-1/2">
          <SearchUserInput />
        </div>

        <div
          class="w-full md:w-auto flex flex-col md:flex-row space-y-2 md:space-y-0 items-stretch md:items-center justify-end md:space-x-3 flex-shrink-0"
        >
          <button @click="showAddModal = true" class="btn btn-primary">
            <svg class="h-4 w-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
              <path
                clip-rule="evenodd"
                fill-rule="evenodd"
                d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
              />
            </svg>
            Adicionar Usuário
          </button>

          <div ref="batchDropdownRef" class="relative">
            <button
              @click="isBatchDropdownOpen = !isBatchDropdownOpen"
              class="btn btn-batch w-full"
              :disabled="!canBatchActions"
            >
              Ações em lote
              <svg
                class="-mr-1 ml-1.5 w-5 h-5 batch-chevron"
                :class="{ open: isBatchDropdownOpen }"
                fill="currentColor"
                viewBox="0 0 20 20"
                aria-hidden="true"
              >
                <path
                  clip-rule="evenodd"
                  fill-rule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                />
              </svg>
            </button>

            <div v-if="isBatchDropdownOpen" class="dropdown-menu">
              <div class="py-1">
                <a
                  href="#"
                  role="button"
                  @click.prevent="tryOpenBatchDeleteModal"
                  class="dropdown-item danger-item"
                >
                  <i class="fas fa-trash-alt"></i>
                  <span>Apagar selecionados</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="table-wrapper">
        <table class="main-table">
          <thead>
            <tr>
              <th style="width: 4%">
                <input
                  id="checkbox-all"
                  type="checkbox"
                  v-model="isAllSelectedOnPage"
                  class="checkbox-style"
                  :disabled="!anyEditableOnPage"
                  :class="{ 'disabled-item': !anyEditableOnPage }"
                />
              </th>

              <th style="width: 60px">Ações</th>
              <th style="width: 5%">Id</th>
              <th style="width: 30%">Nome</th>
              <th style="width: 15%">Email</th>
              <th style="width: 9%">Status</th>

              <th style="width: 7%">Usuários</th>
              <th style="width: 7%">Textos</th>
              <th style="width: 7%">Notas</th>
              <th style="width: 7%">Último IP</th>
              <th style="width: 10%">Localização</th>

              <th style="width: 15%">Criação</th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="(user, index) in !isLoadingTable ? userStore.users : []"
              :key="user.userId"
              @click="handleRowClick(index)"
              class="clickable-row"
            >
              <td @click.stop>
                <input
                  :class="{ 'disabled-item': !canEdit(user) }"
                  type="checkbox"
                  :value="user.userId"
                  :checked="userStore.selectedUserIds?.includes(user.userId)"
                  @change="toggleSelection(user.userId)"
                  class="checkbox-style"
                  :disabled="!canEdit(user)"
                />
              </td>

              <td @click.stop class="action-cell-container">
                <div class="row-actions">
                  <button
                    @click="toggleRowDropdown(user.userId)"
                    class="btn-icon btn-soft-brand"
                    title="Menu de Ações"
                  >
                    <i class="fas fa-ellipsis-h fa-lg"></i>
                  </button>
                </div>

                <div
                  v-if="openDropdownId === user.userId"
                  class="row-dropdown-menu"
                >
                  <ul>
                    <li
                      :class="{ 'disabled-item': !canEdit(user) }"
                      @click="canEdit(user) && handleEdit(index)"
                    >
                      <i class="fas fa-pencil-alt"></i><span>Editar</span>
                    </li>

                    <li
                      :class="{ 'disabled-item': !canView(user) }"
                      @click="canView(user) && handleView(index)"
                    >
                      <i class="fas fa-eye"></i><span>Visualizar</span>
                    </li>

                    <li
                      class="danger-item"
                      :class="{ 'disabled-item': !canDelete(user) }"
                      @click="canDelete(user) && handleDelete(index)"
                    >
                      <i class="fas fa-trash-alt"></i><span>Apagar</span>
                    </li>
                  </ul>
                </div>
              </td>

              <td>
                <span :title="user.userId">{{ user.userId }}</span>
              </td>

              <td class="cell-text-center">
                <span class="user-name-cell" :title="user.userName">
                  <span class="user-avatar">
                    <span
                      v-if="getAvatarUrl(resolveUserPhotoPath(user))"
                      class="user-avatar-img"
                      :style="{
                        backgroundImage: `url('${getAvatarUrl(
                          resolveUserPhotoPath(user)
                        )}')`,
                      }"
                    ></span>

                    <i v-else class="fas fa-user user-avatar-icon"></i>
                  </span>

                  <span class="user-name-text">
                    {{ toTitleCase(user.userName) }}
                  </span>
                </span>
              </td>

              <td class="cell-text-center">
                <span :title="user.email">{{ user.email }}</span>
              </td>

              <td>
                <span :class="getUserStatusClass(user.status)">
                  {{ getUserStatusLabel(user.status) }}
                </span>
              </td>

              <td class="cell-text-center">
                <span :title="user.groupUserCount">{{
                  user.groupUserCount
                }}</span>
              </td>

              <td class="cell-text-center">
                <span :title="user.activeTextsCount">{{
                  user.activeTextsCount
                }}</span>
              </td>

              <td class="cell-text-center">
                <span :title="user.activeNotesCount">{{
                  user.activeNotesCount
                }}</span>
              </td>

              <td>
                <span :title="user.lastIpAddress ?? '—'">
                  {{ user.lastIpAddress ?? "—" }}
                </span>
              </td>

              <td>
                <span :title="user.ipLocation ?? '—'">
                  {{ user.ipLocation ?? "—" }}
                </span>
              </td>

              <td>
                <span :title="formatDateTimeForDisplay(user.createdAt)">
                  {{ formatDateTimeForDisplay(user.createdAt) }}
                </span>
              </td>

              
            </tr>

            <tr v-if="isLoadingTable" class="empty-row-state">
              <td colspan="12" class="empty-row empty-row-state">
                <div class="empty-state-content">
                  <svg class="empty-spinner" viewBox="0 0 24 24" fill="none">
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    />
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    />
                  </svg>
                  <p>Carregando usuários...</p>
                </div>
              </td>
            </tr>

            <tr
              v-else-if="(userStore.totalUsersCount ?? 0) === 0"
              class="empty-row-state"
            >
              <td colspan="12" class="empty-row empty-row-state">
                <div class="empty-state-content">
                  <img src="/img/lupa.webp" alt="" width="38" height="40" class="empty-icon-img" />
                  <p>Nenhum usuário encontrado.</p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <div
          v-if="!isLoadingTable && (userStore.totalUsersCount ?? 0) > 0"
          class="user-pagination-row"
        >
          <div class="user-pagination-left">
            <span class="user-pagination-label">Mostrando</span>
            <PaginationInfo
              :current-page="currentPage"
              :page-size="pageSize"
              :total-count="userStore.totalUsersCount ?? 0"
            />
            <span>registros</span>
          </div>

          <PaginationButtons
            :current-page="currentPage"
            :page-size="pageSize"
            :total-count="userStore.totalUsersCount ?? 0"
            :total-pages="userStore.totalUsersPages"
            @change-page="(page) => (currentPage = page)"
          />
        </div>
      </div>
    </div>
  </div>

  <Teleport to="body">
    <AddUserForm
      v-if="showAddModal"
      @close="showAddModal = false"
      @refresh="handleUsersChanged({ goToFirstPage: true, closeAdd: true })"
    />

    <EditUserForm
      v-if="showEditModal"
      :userId="userStore.selectedUser?.userId"
      @close="showEditModal = false"
      @refresh="handleUsersChanged({ goToFirstPage: false, closeEdit: true })"
    />

    <DeleteUserForm
      v-if="showDeleteModal"
      :user="userStore.selectedUser"
      @close="showDeleteModal = false"
      @refresh="
        handleUsersChanged({
          goToFirstPage: true,
          closeDelete: true,
          clearSelection: true,
        })
      "
    />

    <BatchDeleteUserForm
      v-if="showBatchDeleteModal"
      :userIds="userStore.selectedUserIds"
      @close="showBatchDeleteModal = false"
      @refresh="
        handleUsersChanged({
          goToFirstPage: true,
          closeBatchDelete: true,
          clearSelection: true,
        })
      "
    />

    <UserCardModal
      v-if="showCardModal"
      :show="showCardModal"
      :userId="selectedUserId"
      @close="showCardModal = false"
    />

    <UserHelpModal
      v-if="showHelpModal"
      :show="showHelpModal"
      @close="showHelpModal = false"
    />
  </Teleport>

  <Teleport to="body">
    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
              <path
                v-if="toastType === 'error'"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <div class="toast-content">
            <p class="toast-title">
              {{ toastType === "error" ? "Erro ao salvar" : "Sucesso!" }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>

          <button
            type="button"
            class="toast-close hover:bg-black/10 rounded-full p-1 transition"
            @click="showToast = false"
            aria-label="Fechar notificação"
          >
            &times;
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>
@media (min-width: 768px) {
  .action-bar {
    flex-direction: row;
    align-items: center;
  }
}

.cell-text-center {
  text-align: left;
}

.status-tag {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 500;
  border-radius: 9999px;
}

.status-red {
  background-color: #ffc9c9;
  color: #c92a2a;
}
.status-green {
  background-color: #b2f2bb;
  color: #2b8a3e;
}
.status-yellow {
  background-color: var(--warning-bg);
  color: #854d0e;
}
.status-purple {
  background-color: #e9d5ff;
  color: #6b21a8;
}

.user-pagination-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1.5rem 1rem;
  font-size: 0.82rem;
}

.user-pagination-left {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.user-pagination-label {
  padding-top: 2px;
}

.empty-state-content {
  width: 100%;
  margin: 0 auto;
}

/* ── Avatar na coluna Nome ─────────────────────────────────────────────── */
.user-name-cell {
  display: inline-flex !important;
  align-items: center;
  justify-content: flex-start;
  gap: 0.45rem;
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

.user-avatar {
  width: 24px;
  height: 24px;
  border-radius: 999px;
  background-color: var(--border-color);
  color: var(--text-muted);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex: 0 0 24px;
}

.user-avatar-img {
  width: 100%;
  height: 100%;
  display: block;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.user-avatar-icon {
  font-size: 0.72rem;
  line-height: 1;
}

.user-name-text {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>